// panels/pvc/PvcDevice.js —— 光储充 · 3.2 设备监控（需求文档 p4–p12）
// 3.2.1 电表 / 3.2.2 光伏逆变器 / 3.2.3 PCS / 3.2.4 BMS（电堆 + 电池簇/单体）
// 3.2.5 温控（空调 + 液冷）/ 3.2.6 充电桩（直连 / 电表采集）/ 3.2.7 其他设备（IO/传感器/测控）
// 差异 #11：独有「充电桩（直连 / 电表采集）」与「3.2.5 温控（空调 + 液冷）」，并含光伏逆变器。
// 差异 #7：单体视图选项 = 分布图 / 表格 / 柱状图。
// 通用要求：离线 / 延迟在设备名称旁显示并附数据最后更新时间；未采集数据显示「-」。
import React, { useState } from 'react';
import { Segmented, Select, Table, Button, Tag, Modal } from 'antd';
import ScrollTabs from '../../../../components/ScrollTabs';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ReferenceLine,
} from 'recharts';
import { getDevices } from '../../../../config/stationData';
import { getPvBranches, getChargerGuns, IO_DEVICES, SENSORS, MEASURES } from './data';
import './pvc.css';

const AXIS = { fill: 'rgba(255,255,255,0.5)', fontSize: 11 };
const TT = { background: '#0d1f44', border: '1px solid rgba(0,180,216,0.35)', borderRadius: 8, color: '#fff' };
const GRID = 'rgba(255,255,255,0.08)';

const OK_STATES = ['正常', '运行', '并网', '充电中', '待机', '静置', '放电', '充电', '限功率', '制冷', '自动', '送风', '除湿', '离网'];
const isOk = (s) => OK_STATES.includes(s);
const dash = (v) => (v === undefined || v === null || v === '' ? '-' : v);

const Card = ({ name, state, alarm, tags, rows, children, footer, update }) => {
  const bad = !isOk(state) || alarm;
  return (
    <div className={`dev-card${bad ? ' fault' : ''}`}>
      <div className="dev-card-head">
        <span className="dev-name">
          {name}
          {!isOk(state) ? <Tag color="red" style={{ marginLeft: 8 }}>{state}</Tag> : null}
        </span>
        <span className="dev-status">
          <span className={`dot ${bad ? 'fault' : 'online'}`} />{alarm || state}
        </span>
      </div>
      {update ? <div className="pvc-update">数据更新：{update}</div> : null}
      {tags ? <div className="dev-tags">{tags}</div> : null}
      {rows ? (
        <div className="dev-kv">
          {rows.map(([k, v]) => (
            <div className="dev-kv-row" key={k}>
              <span className="dev-kv-k">{k}</span>
              <span className="dev-kv-v">{dash(v)}</span>
            </div>
          ))}
        </div>
      ) : null}
      {children}
      {footer ? <div className="dev-sub">{footer}</div> : null}
    </div>
  );
};

const PvcDevice = ({ station }) => {
  const dev = getDevices(station.id);
  const [cat, setCat] = useState('meter');
  const [view, setView] = useState('dist');
  const [param, setParam] = useState('voltage');
  const [rackIdx, setRackIdx] = useState(0);
  const [clusterIdx, setClusterIdx] = useState(0);
  const [morePcs, setMorePcs] = useState(null);

  const update = '2026-10-12 10:31:20';

  // ---------- 3.2.1 电表 ----------
  const meterCards = dev.meters.map((m) => (
    <Card
      key={m.id}
      name={m.name}
      state={m.state}
      update={update}
      tags={[<Tag key="w" color="blue">{m.wiring}</Tag>, <Tag key="id">{m.id}</Tag>]}
      rows={[
        ['A 相电压', `${m.u[0]} V`], ['B 相电压', `${m.u[1]} V`], ['C 相电压', `${m.u[2]} V`],
        ['A 相电流', `${m.i[0]} A`], ['B 相电流', `${m.i[1]} A`], ['C 相电流', `${m.i[2]} A`],
        ['有功功率', `${m.p} kW`], ['无功功率', `${m.q} kVar`],
        ['功率因数', m.pf], ['频率', `${m.freq} Hz`],
        ['正向总电能', `${m.ep} kWh`], ['综合倍率', m.id === 'M-04' ? 30 : 60],
      ]}
    />
  ));
  const meterEnergy = dev.meters.map((m) => ({
    key: m.id, name: m.name, ep: m.ep,
    sharp: +(m.ep * 0.12).toFixed(1), peak: +(m.ep * 0.30).toFixed(1),
    flat: +(m.ep * 0.37).toFixed(1), valley: +(m.ep * 0.21).toFixed(1),
    day: +(m.ep * 0.008).toFixed(1),
  }));

  // ---------- 3.2.2 光伏逆变器 ----------
  const pvCards = dev.pv.map((p) => (
    <Card
      key={p.id}
      name={p.name}
      state={p.state}
      update={update}
      footer="支路数据"
      tags={[<Tag key="run" color={p.running ? 'green' : 'default'}>工作状态：{p.running ? '运行' : '停机'}</Tag>, <Tag key="mp">MPPT {p.mppt} 路</Tag>]}
      rows={[
        ['直流功率', `${p.dcPower} kW`], ['交流功率', `${p.acPower} kW`],
        ['当日发电量', `${p.todayEnergy} kWh`], ['直流电压', `${p.dcVoltage} V`],
        ['转换效率', `${p.efficiency} %`], ['设备状态', p.state],
      ]}
    >
      <div className="dev-kv" style={{ marginTop: 10 }}>
        {getPvBranches(p).map((b) => (
          <div className="dev-kv-row" key={b.key}>
            <span className="dev-kv-k">{b.branch}</span>
            <span className="dev-kv-v">{b.voltage} V · {b.current} A</span>
          </div>
        ))}
      </div>
    </Card>
  ));

  // ---------- 3.2.3 PCS ----------
  const pcsCards = dev.pcs.map((p) => (
    <Card
      key={p.id}
      name={p.name}
      state={p.alarm ? '告警' : p.running ? '正常' : '离网'}
      alarm={p.alarm}
      update={update}
      footer="支路数据"
      tags={[
        <Tag key="grid" color={p.grid === '并网' ? 'green' : 'orange'}>并网状态：{p.grid}</Tag>,
        <Tag key="mode">控制模式:{p.mode}</Tag>,
      ]}
      rows={[
        ['有功功率', `${p.p} kW`], ['无功功率', `${p.q} kVar`],
        ['功率因数', p.pf], ['频率', `${p.freq} Hz`],
        ['模块温度', `${p.moduleTemp} ℃`], ['环境温度', `${p.envTemp} ℃`],
      ]}
    >
      <div className="dev-kv" style={{ marginTop: 10 }}>
        {p.branches.map((b) => (
          <div className="dev-kv-row" key={b.name}>
            <span className="dev-kv-k">{b.name}</span>
            <span className="dev-kv-v">{b.p} kW · {b.u} V · {b.i} A</span>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 10, textAlign: 'right' }}>
        <Button size="small" onClick={() => setMorePcs(p)}>更多参数</Button>
      </div>
    </Card>
  ));

  // ---------- 3.2.4 BMS ----------
  // p8 电堆级别：状态栏 = 充放电状态 + 工作状态（未知/就绪/故障/维护/禁充/禁放/禁充放）
  const rackCards = dev.racks.map((r) => (
    <Card
      key={r.id}
      name={r.name}
      state={r.state}
      alarm={r.alarm}
      tags={[
        <Tag key="c" color={r.state === '静置' ? 'default' : r.state === '充电' ? 'cyan' : 'green'}>充放电状态：{r.state}</Tag>,
        <Tag key="w" color={r.workState === '就绪' ? 'green' : r.workState === '故障' ? 'red' : 'orange'}>工作状态：{r.workState}</Tag>,
      ]}
      rows={[
        ['电压', `${r.voltage} V`], ['电流', `${r.current} A`],
        ['温度', `${r.temp} ℃`], ['可用电量', `${r.available} kWh`],
        ['SOC', `${r.soc} %`], ['SOH', `${r.soh.toFixed(1)} %`],
      ]}
    />
  ));

  // p9 电池簇 / 单体：单体视图支持切换电池簇（演示：每堆 2 簇，各占一半单体）
  const CLUSTERS_PER_RACK = 2;
  const CELLS_PER_CLUSTER = Math.floor(dev.cells.length / CLUSTERS_PER_RACK);
  const clusterOptions = Array.from({ length: CLUSTERS_PER_RACK }, (_, i) => ({ value: i, label: `${i + 1} 号簇` }));
  const curRack = dev.racks[rackIdx];
  const cells = dev.cells.slice(clusterIdx * CELLS_PER_CLUSTER, (clusterIdx + 1) * CELLS_PER_CLUSTER);
  const field = param === 'temp' ? 'temp' : param === 'soc' ? 'soc' : param === 'soh' ? 'soh' : 'voltage';
  const unit = { voltage: 'V', temp: '℃', soc: '%', soh: '%' }[param];
  const values = cells.map((c) => c[field]);
  const maxV = Math.max(...values);
  const minV = Math.min(...values);
  const avgV = +(values.reduce((a, b) => a + b, 0) / values.length).toFixed(param === 'voltage' ? 3 : 1);
  const maxCell = cells.find((c) => c[field] === maxV);
  const minCell = cells.find((c) => c[field] === minV);

  const cellColor = (v) => {
    if (param !== 'temp') return '';
    if (v >= 32) return 'fault';
    if (v >= 30) return 'warn';
    return '';
  };
  const chartData = cells.map((c) => ({ no: c.no, value: c[field] }));

  const cellColumns = [
    { title: '单体号', dataIndex: 'no', width: 90 },
    { title: `电压 (V)`, dataIndex: 'voltage', render: (v, r) => (r.no === 38 ? <span style={{ color: '#ff5a7f' }}>{v}</span> : v) },
    { title: '温度 (℃)', dataIndex: 'temp', render: (v, r) => (r.no === 61 ? <span style={{ color: '#ff5a7f' }}>{v}</span> : v) },
    { title: 'SOC (%)', dataIndex: 'soc' },
    { title: 'SOH (%)', dataIndex: 'soh' },
  ];

  // ---------- 3.2.5 温控（空调 + 液冷）----------
  const hvacCards = dev.hvac.map((h) => {
    const tempBad = h.alarm || h.state !== '正常';
    return (
      <Card
        key={h.id}
        name={h.name}
        state={h.state}
        alarm={h.alarm}
        update={update}
        tags={[
          <Tag key="run" color={h.running ? 'green' : 'default'}>工作状态：{h.running ? '运行' : '停止'}</Tag>,
          <Tag key="mode">工作模式:{h.mode}</Tag>,
        ]}
        rows={[
          ['设定温度', `${h.setTemp} ℃`], ['回风温度', `${h.returnTemp} ℃`],
          ['出风温度', `${h.supplyTemp} ℃`], ['环境湿度', `${h.humidity} %RH`],
          ['风机', h.fan], ['压缩机', h.compressor],
        ]}
      >
        <div className="pvc-temp-block" data-alarm={tempBad ? '1' : '0'}>
          <span className="pvc-temp-label">温度告警色块</span>
          <span className={`pvc-temp-cell ${tempBad ? 'fault' : 'ok'}`}>{h.returnTemp} ℃</span>
        </div>
      </Card>
    );
  });
  const liquid = dev.liquid;
  const liquidCard = (
    <Card
      key={liquid.id}
      name={liquid.name}
      state={liquid.state}
      update={update}
      tags={[<Tag key="run" color={liquid.running ? 'green' : 'default'}>运行状态：{liquid.running ? '运行' : '停止'}</Tag>, <Tag key="mode">运行模式:{liquid.mode}</Tag>]}
      rows={[
        ['出水温度', `${liquid.supplyTemp} ℃`], ['回水温度', `${liquid.returnTemp} ℃`],
        ['设定温度', `${liquid.setTemp} ℃`], ['泵', liquid.pump],
        ['风机', liquid.fan], ['系统压力', `${liquid.pressure} MPa`],
      ]}
    />
  );

  // ---------- 3.2.6 充电桩（直连 / 电表采集 · 差异 #11）----------
  const chargerCards = dev.charger.map((c) => {
    const guns = getChargerGuns(c);
    return (
      <Card
        key={c.id}
        name={c.name}
        state={c.state === '离线' ? '离线' : '正常'}
        update={update}
        tags={[
          <Tag key="col" color={c.collect === '直连' ? 'cyan' : 'geekblue'}>采集方式：{c.collect}</Tag>,
          <Tag key="gun">充电枪 {c.guns} 把</Tag>,
        ]}
        rows={[
          ['工作状态', c.state === '离线' ? '离线' : dash(c.state)],
          ['电压', c.power ? '402 V' : '-'],
          ['电流', c.power ? `${(c.power / 0.4).toFixed(1)} A` : '-'],
          ['功率', c.power ? `${c.power} kW` : '-'],
          ['当日充电量', `${c.todayEnergy} kWh`],
        ]}
      >
        {guns ? (
          <div style={{ marginTop: 10 }}>
            {guns.map((g) => (
              <div className="pvc-gun-head" key={g.key}>
                <span>{g.gun}</span>
                <span>
                  <Tag color={g.state === '充电中' ? 'green' : g.state === '故障状态' ? 'red' : 'default'}>{g.state}</Tag>
                  <span style={{ color: 'rgba(255,255,255,0.7)' }}>{g.power ? `${g.power} kW` : '-'}</span>
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="dev-sub">电表采集充电桩：仅上报电压 / 电流 / 功率 / 充电量，无枪级状态</div>
        )}
      </Card>
    );
  });

  // ---------- 3.2.7 其他设备 ----------
  const otherCards = (
    <>
      {dev.others.map((o) => (
        <Card
          key={o.id}
          name={o.name}
          state={o.state}
          update={update}
          rows={o.inVoltage ? [
            ['运行状态', o.running ? '运行' : '停止'], ['输入电压', `${o.inVoltage} V`],
            ['输出电压', `${o.outVoltage} V`], ['功率', `${o.power} kW`],
          ] : [
            ['运行状态', o.running ? '运行' : '停止'], ['电压', `${o.voltage} V`],
            ['电池电量', `${o.battery} %`], ['负载', `${o.load} %`],
          ]}
        />
      ))}
      {IO_DEVICES.map((io) => (
        <Card
          key={io.id}
          name={io.name}
          state={io.state}
          update={update}
          footer="DI / DO 端口"
        >
          <div className="pvc-dio">
            {[...io.di.map(([p, n, v]) => [`${p} ${n}`, v]), ...io.doPorts.map(([p, n, v]) => [`${p} ${n}`, v])].map(([k, v]) => (
              <div className="pvc-dio-row" key={k}><span>{k}</span><span style={{ color: v === '闭合' ? '#52e8b0' : 'rgba(255,255,255,0.7)' }}>{v}</span></div>
            ))}
          </div>
        </Card>
      ))}
      {SENSORS.map((s) => (
        <Card key={s.id} name={s.name} state={s.state} update={update}
          rows={s.rows.map(([k, v]) => [k, v])} />
      ))}
      {MEASURES.map((m) => (
        <Card key={m.id} name={m.name} state={m.state} update={update}
          rows={m.rows.map(([k, v]) => [k, v])} />
      ))}
    </>
  );

  const tabItems = [
    {
      key: 'meter',
      label: '电表',
      children: (
        <>
          <div className="panel">
            <div className="panel-title">电能统计<small>累计正向总电能（总/尖/峰/平/谷）· 日正向总电能 · kWh</small></div>
            <Table
              size="small"
              rowKey="key"
              pagination={false}
              dataSource={meterEnergy}
              columns={[
                { title: '电表', dataIndex: 'name' },
                { title: '累计正向(总)', dataIndex: 'ep' },
                { title: '尖', dataIndex: 'sharp' },
                { title: '峰', dataIndex: 'peak' },
                { title: '平', dataIndex: 'flat' },
                { title: '谷', dataIndex: 'valley' },
                { title: '日正向(总)', dataIndex: 'day' },
              ]}
            />
          </div>
          <div className="dev-grid">{meterCards}</div>
        </>
      ),
    },
    { key: 'pv', label: '光伏逆变器', children: <div className="dev-grid">{pvCards}</div> },
    { key: 'pcs', label: 'PCS', children: <div className="dev-grid">{pcsCards}</div> },
    {
      key: 'bms',
      label: 'BMS',
      children: (
        <>
          <div className="panel">
            <div className="panel-title">电堆级别<small>充放电状态 / 工作状态 / 电压 / 电流 / 温度 / 可用电量 / SOC / SOH</small></div>
            <div className="dev-grid">{rackCards}</div>
          </div>
          <div className="panel">
            <div className="panel-title">
              <span>电池簇 / 单体<small style={{ marginLeft: 8 }}>切换电池堆 / 电池簇查看簇内单体数据</small></span>
              <span>
                <Select
                  size="small"
                  value={rackIdx}
                  onChange={(v) => { setRackIdx(v); setClusterIdx(0); }}
                  style={{ width: 130, marginRight: 8 }}
                  options={dev.racks.map((r, i) => ({ value: i, label: r.name }))}
                />
                <Select
                  size="small"
                  value={clusterIdx}
                  onChange={setClusterIdx}
                  style={{ width: 110, marginRight: 8 }}
                  options={clusterOptions}
                />
                <Segmented
                  size="small"
                  value={view}
                  onChange={setView}
                  options={[{ value: 'dist', label: '分布图' }, { value: 'table', label: '表格' }, { value: 'bar', label: '柱状图' }]}
                />
              </span>
            </div>
            <div className="dev-grid">
              <Card
                key="cluster"
                name={`${curRack.name} · ${clusterIdx + 1} 号簇`}
                state={curRack.state}
                alarm={curRack.alarm}
                tags={[
                  <Tag key="c" color={curRack.state === '静置' ? 'default' : curRack.state === '充电' ? 'cyan' : 'green'}>充放电状态：{curRack.state}</Tag>,
                  <Tag key="w" color={curRack.workState === '就绪' ? 'green' : curRack.workState === '故障' ? 'red' : 'orange'}>工作状态：{curRack.workState}</Tag>,
                  <Tag key="p" color={curRack.contactorPos === '闭合' ? 'green' : 'default'}>主正接触器：{curRack.contactorPos}</Tag>,
                  <Tag key="n" color={curRack.contactorNeg === '闭合' ? 'green' : 'default'}>主负接触器：{curRack.contactorNeg}</Tag>,
                ]}
                rows={[
                  ['电压', `${curRack.voltage} V`], ['电流', `${curRack.current} A`],
                  ['温度', `${curRack.temp} ℃`], ['可用电量', `${curRack.available} kWh`],
                  ['SOC', `${curRack.soc} %`], ['SOH', `${curRack.soh.toFixed(1)} %`],
                ]}
              />
            </div>
            <div className="dev-seg-bar">
              <Segmented
                value={param}
                onChange={setParam}
                options={[{ value: 'voltage', label: '电压' }, { value: 'temp', label: '温度' }, { value: 'soc', label: 'SOC' }, { value: 'soh', label: 'SOH' }]}
              />
            </div>
            <div className="strat-grid">
              <div className="strat-item"><div className="kpi-label">最大值</div><div className="kpi-value">{maxV}<span className="unit">{unit}</span></div><div className="kpi-foot">{maxCell ? `${maxCell.no} 号单体` : '-'}</div></div>
              <div className="strat-item"><div className="kpi-label">最小值</div><div className="kpi-value">{minV}<span className="unit">{unit}</span></div><div className="kpi-foot">{minCell ? `${minCell.no} 号单体` : '-'}</div></div>
              <div className="strat-item"><div className="kpi-label">平均值</div><div className="kpi-value">{avgV}<span className="unit">{unit}</span></div><div className="kpi-foot">共 {cells.length} 个单体</div></div>
            </div>

            {view === 'dist' ? (
              <div className="pvc-cell-grid">
                {cells.map((c) => (
                  <div className={`pvc-cell ${cellColor(c.temp)}`} key={c.no} title={`${c.no} 号：${c[field]}${unit}`}>{c.no}</div>
                ))}
              </div>
            ) : null}

            {view === 'table' ? (
              <Table
                size="small"
                rowKey="no"
                dataSource={cells}
                columns={cellColumns}
                pagination={{ pageSize: 20, showSizeChanger: false }}
                scroll={{ y: 320 }}
              />
            ) : null}

            {view === 'bar' ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                  <XAxis dataKey="no" interval={3} tick={AXIS} axisLine={false} tickLine={false} />
                  <YAxis domain={['dataMin', 'dataMax']} tick={AXIS} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={TT} />
                  <ReferenceLine y={avgV} stroke="#f4c542" strokeDasharray="5 4" label={{ value: `平均 ${avgV}`, fill: '#f4c542', fontSize: 11, position: 'insideTopRight' }} />
                  <Bar dataKey="value" name={`单体${param === 'temp' ? '温度' : '电压'}`} barSize={6}>
                    {chartData.map((d) => (
                      <Cell key={d.no} fill={param === 'temp' && d.value >= 32 ? '#ff5a7f' : param === 'temp' && d.value >= 30 ? '#f4c542' : '#4dd8ff'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : null}
          </div>
        </>
      ),
    },
    {
      key: 'hvac',
      label: '温控',
      children: (
        <>
          <div className="panel">
            <div className="panel-title">空调<small>工作状态 / 工作模式 · 温度告警时显示红色色块</small></div>
            <div className="dev-grid">{hvacCards}</div>
          </div>
          <div className="panel">
            <div className="panel-title">液冷<small>运行状态 / 运行模式 · 出水温度 / 回水温度 / 泵 / 风机</small></div>
            <div className="dev-grid">{liquidCard}</div>
          </div>
        </>
      ),
    },
    { key: 'charger', label: '充电桩', children: <div className="dev-grid">{chargerCards}</div> },
    { key: 'other', label: '其他设备', children: <div className="dev-grid">{otherCards}</div> },
  ];

  // 子页切换对齐光储充「控制中心」顶部胶囊 Tabs
  return (
    <>
      <ScrollTabs activeKey={cat} onChange={setCat} items={tabItems} />
      <Modal
        title={morePcs ? `${morePcs.name} · 更多参数` : '更多参数'}
        open={!!morePcs}
        onCancel={() => setMorePcs(null)}
        footer={null}
        destroyOnHidden
      >
        {morePcs ? (
          <div className="dev-kv">
            <div className="dev-kv-row"><span className="dev-kv-k">三相电压</span><span className="dev-kv-v">{morePcs.uabc.join(' / ')} V</span></div>
            <div className="dev-kv-row"><span className="dev-kv-k">三相电流</span><span className="dev-kv-v">{morePcs.iabc.join(' / ')} A</span></div>
            <div className="dev-kv-row"><span className="dev-kv-k">今日充电量</span><span className="dev-kv-v">{morePcs.chargeToday} kWh</span></div>
            <div className="dev-kv-row"><span className="dev-kv-k">今日放电量</span><span className="dev-kv-v">{morePcs.dischargeToday} kWh</span></div>
            <div className="dev-kv-row"><span className="dev-kv-k">并网状态</span><span className="dev-kv-v">{morePcs.grid}</span></div>
            <div className="dev-kv-row"><span className="dev-kv-k">控制模式</span><span className="dev-kv-v">{morePcs.mode}</span></div>
          </div>
        ) : null}
      </Modal>
    </>
  );
};

export default PvcDevice;
